import { useState, type FormEvent } from "react";
import { useLang, translations } from "../i18n";

interface WaitlistFormProps {
  variant: "dark" | "light";
}

export function WaitlistForm({ variant }: WaitlistFormProps) {
  const { lang } = useLang();
  const t = translations.waitlistForm;
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    contactName: "",
    company: "",
    role: "",
    volume: "",
    useCase: "",
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formData.email.trim() || !formData.company.trim()) return;

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, lang }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Request failed");
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(lang === "en" ? "Something went wrong. Please try again." : "Algo deu errado. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  function update(field: keyof typeof formData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  if (submitted) {
    return (
      <div className="text-center py-4">
        <p className="text-base font-semibold text-[var(--primary)] mb-1">
          {t.successTitle[lang]}
        </p>
        <p
          className={`text-sm ${
            variant === "dark"
              ? "text-white/40"
              : "text-[var(--muted-foreground)]"
          }`}
        >
          {t.successDescription[lang]}
        </p>
      </div>
    );
  }

  const isDark = variant === "dark";
  const inputClass = `w-full px-4 py-3 text-sm rounded-lg outline-none transition-all ${
    isDark
      ? "bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-white/30 focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
      : "bg-white border border-[var(--border)] text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
  }`;
  const selectClass = `${inputClass} appearance-none cursor-pointer`;

  const roleOptions = t.roleOptions[lang];
  const volumeOptions = t.volumeOptions[lang];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          required
          placeholder={t.contactName[lang]}
          value={formData.contactName}
          onChange={(e) => update("contactName", e.target.value)}
          className={inputClass}
        />
        <input
          type="email"
          required
          placeholder={t.email[lang]}
          value={formData.email}
          onChange={(e) => update("email", e.target.value)}
          className={inputClass}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          required
          placeholder={t.company[lang]}
          value={formData.company}
          onChange={(e) => update("company", e.target.value)}
          className={inputClass}
        />
        <select
          value={formData.role}
          onChange={(e) => update("role", e.target.value)}
          className={selectClass}
        >
          {roleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select
          value={formData.volume}
          onChange={(e) => update("volume", e.target.value)}
          className={selectClass}
        >
          {volumeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder={t.useCase[lang]}
          value={formData.useCase}
          onChange={(e) => update("useCase", e.target.value)}
          className={inputClass}
        />
      </div>
      {error && (
        <p className={`text-sm ${isDark ? "text-red-400" : "text-red-500"}`}>
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full sm:w-auto sm:self-start px-7 py-3.5 bg-[var(--primary)] hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold tracking-wide rounded-lg transition-colors duration-150 cursor-pointer"
      >
        {submitting ? (lang === "en" ? "Submitting..." : "Enviando...") : t.submit[lang]}
      </button>
    </form>
  );
}
